import * as THREE from "three";
import { type BoardSize, DEFAULT_BOARD_SIZE } from "../game/constants";
import type { Cell } from "../game/board";
import type { Gem, GemEffect } from "../game/engine";
import type { GameTextures } from "./assets";
import { ease, lerp, Tweener } from "./tweener";
import {
  MESSAGE_FONT_PX,
  SCORE_FONT_PX,
  makeBoardTexture,
  makeFocusTexture,
  makeTextTexture,
} from "./text";

/** Opacity of a blinking gem: 155 / 255 in the original. */
const BLINK_ALPHA = 155 / 255;
const BLINK_MS = 200;
const BOUNCE_UP_MS = 350;
const BOUNCE_DOWN_MS = 150;
const BOUNCE_LIFT = 0.07;

interface GemVisual {
  readonly id: number;
  readonly sprite: THREE.Sprite;
  readonly gem: Gem;
  /** Opacity driven by the blink effect and by tweens. Multiplied by the layer alpha. */
  alpha: number;
  effect: GemEffect;
  bounceUp: boolean;
  blinkDim: boolean;
  nextEffectAt: number;
}

/**
 * Renders the game board with Three.js. Implements the board half of {@link GameView}.
 *
 * Board coordinates use the original's tile space: x grows to the right and y grows downward
 * from the top row. The orthographic camera is y-up, so y is flipped in `worldY`.
 */
export class BoardRenderer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.OrthographicCamera;
  private readonly boardMesh: THREE.Mesh;
  private readonly boardMaterial = new THREE.MeshBasicMaterial();
  private readonly gemLayer = new THREE.Group();
  private readonly effectLayer = new THREE.Group();
  private readonly textLayer = new THREE.Group();
  private readonly tweener = new Tweener();
  private readonly gems = new Map<Gem, GemVisual>();
  private readonly cursors = new Map<number, THREE.Sprite>();
  private readonly texts = new Map<number, THREE.Sprite>();
  private readonly focusSprite: THREE.Sprite;
  private readonly textures: GameTextures;
  private readonly now: () => number;
  private width = DEFAULT_BOARD_SIZE.width;
  private height = DEFAULT_BOARD_SIZE.height;
  private nextId = 1;
  private gemAlpha = 1;

  constructor(canvas: HTMLCanvasElement, textures: GameTextures, now = () => performance.now()) {
    this.textures = textures;
    this.now = now;

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
    this.renderer.setClearColor(0x000000, 1);

    this.camera = new THREE.OrthographicCamera(0, 1, 1, 0, 0.1, 100);
    this.camera.position.set(0, 0, 10);

    this.boardMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.boardMaterial);
    this.setBoardSize(DEFAULT_BOARD_SIZE);
    this.scene.add(this.boardMesh);

    this.gemLayer.renderOrder = 1;
    this.effectLayer.renderOrder = 2;
    this.textLayer.renderOrder = 3;
    this.scene.add(this.gemLayer, this.effectLayer, this.textLayer);

    this.focusSprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: makeFocusTexture(),
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    );
    this.focusSprite.visible = false;
    this.effectLayer.add(this.focusSprite);
  }

  /**
   * Sets the board dimensions for the next game. The camera and board background follow the
   * size, so a non-square board fills its canvas without stretching.
   */
  setBoardSize(size: BoardSize): void {
    this.width = size.width;
    this.height = size.height;

    this.camera.left = 0;
    this.camera.right = this.width;
    this.camera.top = this.height;
    this.camera.bottom = 0;
    this.camera.updateProjectionMatrix();

    this.boardMesh.geometry.dispose();
    this.boardMesh.geometry = new THREE.PlaneGeometry(this.width, this.height);
    this.boardMesh.position.set(this.width / 2, this.height / 2, 0);

    this.boardMaterial.map?.dispose();
    this.boardMaterial.map = makeBoardTexture(this.width, this.height);
  }

  /** Sizes the drawing buffer to `widthPx` by `heightPx` CSS pixels. */
  resize(widthPx: number, heightPx: number): void {
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(widthPx, heightPx, false);
  }

  /** Draws one frame. `now` is a performance.now() timestamp. */
  render(now: number): void {
    this.tweener.update(now);

    for (const visual of this.gems.values()) {
      if (visual.effect !== "none" && now >= visual.nextEffectAt) {
        this.stepEffect(visual, now);
      }
      visual.sprite.material.opacity = visual.alpha * this.gemAlpha;
    }

    this.renderer.render(this.scene, this.camera);
  }

  /** Maps a pointer position to a board cell. Returns null outside the board. */
  cellAt(clientX: number, clientY: number): Cell | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const x = Math.floor(((clientX - rect.left) / rect.width) * this.width);
    const y = Math.floor(((clientY - rect.top) / rect.height) * this.height);
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) return null;
    return { x, y };
  }

  /** Shows the keyboard focus ring on a cell, or hides it with null. */
  setFocusCell(cell: Cell | null): void {
    if (cell === null) {
      this.focusSprite.visible = false;
      return;
    }
    const center = this.cellCenter(cell.x, cell.y);
    this.focusSprite.position.set(center.x, center.y, 2);
    this.focusSprite.scale.set(1, 1, 1);
    this.focusSprite.visible = true;
  }

  dispose(): void {
    this.tweener.cancelPrefix("");
    this.renderer.dispose();
    this.boardMaterial.map?.dispose();
  }

  /** Centre of a tile in world units, with the y axis flipped to match the tile space. */
  private cellCenter(x: number, y: number): { x: number; y: number } {
    return { x: x + 0.5, y: this.worldY(y + 0.5) };
  }

  private worldY(tileY: number): number {
    return this.height - tileY;
  }

  // ---------------------------------------------------------------------------------------------
  // Gems
  // ---------------------------------------------------------------------------------------------

  addGem(gem: Gem): void {
    const center = this.cellCenter(gem.x, gem.y);
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.textures.gems[gem.type],
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    );
    sprite.position.set(center.x, center.y, 1);
    sprite.scale.set(1, 1, 1);
    this.gemLayer.add(sprite);

    this.gems.set(gem, {
      id: this.nextId++,
      sprite,
      gem,
      alpha: 1,
      effect: "none",
      bounceUp: true,
      blinkDim: false,
      nextEffectAt: 0,
    });
  }

  clearGems(): void {
    for (const visual of Array.from(this.gems.values())) {
      this.removeGem(visual);
    }
  }

  moveGem(gem: Gem, x: number, y: number, done: () => void): void {
    const visual = this.gems.get(gem);
    if (!visual) {
      done();
      return;
    }
    const from = visual.sprite.position.clone();
    const to = this.cellCenter(x, y);
    this.tweener.add(
      `g${visual.id}.pos`,
      {
        duration: 200,
        ease: ease.inOutCubic,
        step: (t) => {
          visual.sprite.position.x = lerp(from.x, to.x, t);
          visual.sprite.position.y = lerp(from.y, to.y, t);
        },
        onDone: done,
        onCancel: done,
      },
      this.now(),
    );
  }

  fallGem(gem: Gem, toY: number, delayMs: number, durationMs: number, done: () => void): void {
    const visual = this.gems.get(gem);
    if (!visual) {
      done();
      return;
    }
    const fromY = visual.sprite.position.y;
    const targetY = this.cellCenter(gem.x, toY).y;
    this.tweener.add(
      `g${visual.id}.pos`,
      {
        duration: durationMs,
        delay: delayMs,
        ease: ease.outCubic,
        step: (t) => {
          visual.sprite.position.y = lerp(fromY, targetY, t);
        },
        onDone: done,
        onCancel: done,
      },
      this.now(),
    );
  }

  dropGem(gem: Gem, toY: number, delayMs: number): void {
    const visual = this.gems.get(gem);
    if (!visual) return;
    const fromY = visual.sprite.position.y;
    const targetY = this.cellCenter(gem.x, toY).y;
    this.tweener.add(
      `g${visual.id}.pos`,
      {
        duration: 500,
        delay: delayMs,
        ease: ease.inQuad,
        step: (t) => {
          visual.sprite.position.y = lerp(fromY, targetY, t);
        },
      },
      this.now(),
    );
  }

  /** Gem removal: pops up to 120%, then shrinks away. */
  destroyGem(gem: Gem, done: () => void): void {
    const visual = this.gems.get(gem);
    if (!visual) {
      done();
      return;
    }
    const finish = (): void => {
      this.removeGem(visual);
      done();
    };
    const shrink = (): void => {
      this.tweener.add(
        `g${visual.id}.scale`,
        {
          duration: 150,
          step: (t) => {
            visual.sprite.scale.setScalar(lerp(1.2, 0, t));
          },
          onDone: finish,
          onCancel: finish,
        },
        this.now(),
      );
    };
    this.tweener.add(
      `g${visual.id}.scale`,
      {
        duration: 80,
        ease: ease.outCubic,
        step: (t) => {
          visual.sprite.scale.setScalar(lerp(1, 1.2, t));
        },
        onDone: shrink,
        onCancel: finish,
      },
      this.now(),
    );
  }

  setGemEffect(gem: Gem, effect: GemEffect): void {
    const visual = this.gems.get(gem);
    if (!visual) return;

    this.tweener.cancelPrefix(`g${visual.id}.fx`);
    visual.effect = effect;

    if (effect === "none") {
      visual.alpha = 1;
      visual.sprite.scale.setScalar(1);
      const center = this.cellCenter(gem.x, gem.y);
      visual.sprite.position.set(center.x, center.y, 1);
      return;
    }

    visual.bounceUp = true;
    visual.blinkDim = false;
    this.stepEffect(visual, this.now());
  }

  setGemsOpacity(alpha: number): void {
    this.gemAlpha = alpha;
  }

  setGemsVisible(visible: boolean): void {
    this.gemLayer.visible = visible;
  }

  setEffectsVisible(visible: boolean): void {
    this.effectLayer.visible = visible;
  }

  private stepEffect(visual: GemVisual, now: number): void {
    if (visual.effect === "blink") {
      visual.blinkDim = !visual.blinkDim;
      const from = visual.alpha;
      const to = visual.blinkDim ? BLINK_ALPHA : 1;
      this.tweener.add(
        `g${visual.id}.fx.alpha`,
        {
          duration: BLINK_MS,
          step: (t) => {
            visual.alpha = lerp(from, to, t);
          },
        },
        now,
      );
      visual.nextEffectAt = now + BLINK_MS;
      return;
    }

    if (visual.effect === "bounce") {
      const base = this.cellCenter(visual.gem.x, visual.gem.y);
      const fromY = visual.sprite.position.y;
      const fromScale = visual.sprite.scale.clone();
      const lifted = visual.bounceUp;
      const duration = lifted ? BOUNCE_UP_MS : BOUNCE_DOWN_MS;
      const targetY = lifted ? base.y - BOUNCE_LIFT : base.y;
      const targetScale = lifted ? { x: 1.1, y: 0.9 } : { x: 1, y: 1 };

      this.tweener.add(
        `g${visual.id}.fx.pos`,
        {
          duration,
          ease: ease.inOutQuad,
          step: (t) => {
            visual.sprite.position.y = lerp(fromY, targetY, t);
          },
        },
        now,
      );
      this.tweener.add(
        `g${visual.id}.fx.scale`,
        {
          duration,
          ease: ease.inOutQuad,
          step: (t) => {
            visual.sprite.scale.set(
              lerp(fromScale.x, targetScale.x, t),
              lerp(fromScale.y, targetScale.y, t),
              1,
            );
          },
        },
        now,
      );
      visual.bounceUp = !lifted;
      visual.nextEffectAt = now + duration;
    }
  }

  private removeGem(visual: GemVisual): void {
    if (!this.gems.has(visual.gem)) return;
    this.tweener.cancelPrefix(`g${visual.id}.`);
    this.gemLayer.remove(visual.sprite);
    visual.sprite.material.dispose();
    this.gems.delete(visual.gem);
  }

  // ---------------------------------------------------------------------------------------------
  // Cursor (the selected gem marker)
  // ---------------------------------------------------------------------------------------------

  createCursor(x: number, y: number): number {
    const id = this.nextId++;
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.textures.cursor,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    );
    const center = this.cellCenter(x, y);
    sprite.position.set(center.x, center.y, 2);
    sprite.scale.set(1, 1, 1);
    this.effectLayer.add(sprite);
    this.cursors.set(id, sprite);
    return id;
  }

  moveCursor(cursorId: number, x: number, y: number): void {
    const sprite = this.cursors.get(cursorId);
    if (!sprite) return;
    const from = sprite.position.clone();
    const to = this.cellCenter(x, y);
    this.tweener.add(
      `c${cursorId}.pos`,
      {
        duration: 200,
        ease: ease.inOutCubic,
        step: (t) => {
          sprite.position.x = lerp(from.x, to.x, t);
          sprite.position.y = lerp(from.y, to.y, t);
        },
      },
      this.now(),
    );
  }

  removeCursor(cursorId: number): void {
    const sprite = this.cursors.get(cursorId);
    if (!sprite) return;
    this.tweener.cancelPrefix(`c${cursorId}.`);
    this.effectLayer.remove(sprite);
    sprite.material.dispose();
    this.cursors.delete(cursorId);
  }

  fadeCursor(cursorId: number): void {
    const sprite = this.cursors.get(cursorId);
    if (!sprite) return;
    const remove = (): void => this.removeCursor(cursorId);
    this.tweener.add(
      `c${cursorId}.fade`,
      {
        duration: 200,
        step: (t) => {
          sprite.material.opacity = 1 - t;
        },
        onDone: remove,
        onCancel: remove,
      },
      this.now(),
    );
  }

  // ---------------------------------------------------------------------------------------------
  // Text
  // ---------------------------------------------------------------------------------------------

  showScorePopup(text: string, cx: number, cy: number): void {
    const id = this.nextId++;
    const sprite = this.createTextSprite(
      makeTextTexture(text, 2, 1, SCORE_FONT_PX),
      2,
      1,
      cx,
      this.worldY(cy),
      id,
    );
    const startY = sprite.position.y;
    const startScale = 0.8;

    // zoomin (500 ms, bounce) and fly_away (800 ms up, fading out after 700 ms).
    this.tweener.add(
      `t${id}.scale`,
      {
        duration: 500,
        delay: 50,
        ease: ease.outBounce,
        step: (t) => {
          const s = lerp(startScale, 1, t);
          sprite.scale.set(2 * s, s, 1);
        },
      },
      this.now(),
    );
    sprite.scale.set(2 * startScale, startScale, 1);

    this.tweener.add(
      `t${id}.fly`,
      {
        duration: 800,
        ease: ease.linear,
        step: (t) => {
          sprite.position.y = lerp(startY, startY + 1 / 3, t);
        },
        onDone: () => this.removeText(id),
      },
      this.now(),
    );
    this.tweener.add(
      `t${id}.fade`,
      {
        duration: 100,
        delay: 700,
        step: (t) => {
          sprite.material.opacity = 1 - t;
        },
      },
      this.now(),
    );
  }

  /** Full-board message. Stays until cleared when `lifetimeMs` is 0. */
  showMessage(text: string, lifetimeMs: number): void {
    const id = this.nextId++;
    const sprite = this.createTextSprite(
      makeTextTexture(text, this.width, this.height, MESSAGE_FONT_PX),
      this.width,
      this.height,
      this.width / 2,
      this.height / 2,
      id,
    );
    const startScale = 0.8;
    sprite.scale.set(this.width * startScale, this.height * startScale, 1);

    this.tweener.add(
      `t${id}.scale`,
      {
        duration: 500,
        delay: 50,
        ease: ease.outBack,
        step: (t) => {
          const s = lerp(startScale, 1, t);
          sprite.scale.set(this.width * s, this.height * s, 1);
        },
      },
      this.now(),
    );

    if (lifetimeMs > 0) {
      this.tweener.add(
        `t${id}.fade`,
        {
          duration: 500,
          delay: lifetimeMs,
          step: (t) => {
            sprite.material.opacity = 1 - t;
          },
          onDone: () => this.removeText(id),
        },
        this.now(),
      );
    }
  }

  clearMessages(): void {
    for (const id of Array.from(this.texts.keys())) {
      this.removeText(id);
    }
  }

  private createTextSprite(
    texture: THREE.CanvasTexture,
    widthCells: number,
    heightCells: number,
    cx: number,
    cy: number,
    id: number,
  ): THREE.Sprite {
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    );
    sprite.position.set(cx, cy, 3);
    sprite.scale.set(widthCells, heightCells, 1);
    this.textLayer.add(sprite);
    this.texts.set(id, sprite);
    return sprite;
  }

  private removeText(id: number): void {
    const sprite = this.texts.get(id);
    if (!sprite) return;
    this.tweener.cancelPrefix(`t${id}.`);
    this.textLayer.remove(sprite);
    sprite.material.map?.dispose();
    sprite.material.dispose();
    this.texts.delete(id);
  }
}
